/**
 * The site's spinner.
 *
 * Used in seven places, so the `type` prop is unchanged. What changed is what
 * it looks like: it was eight bars sharing one `animate-pulse`, which fade in
 * and out together rather than travelling around the circle - at 20% opacity
 * it read as a smudge rather than as progress. The bars now fade in sequence,
 * so the motion actually goes somewhere, and `brand` renders in Palmidos green
 * for the places that sit on our own dark surfaces.
 *
 * Honours `prefers-reduced-motion`: the rotation stops and the ring simply
 * holds, rather than animating for someone who asked it not to.
 */
export default function Loader({
  type,
  size = 40,
}: {
  type: "light" | "dark" | "brand";
  /** Outer diameter in px. The bars scale with it. */
  size?: number;
}) {
  const color =
    type === "light" ? "#ffffff" : type === "brand" ? "#24D56D" : "#1f2937";
  const bars = 8;

  return (
    <div
      role="status"
      aria-label="Loading"
      className="relative flex items-center justify-center motion-safe:animate-[spin_1s_steps(8)_infinite]"
      style={{ width: size, height: size }}
    >
      {[...Array(bars)].map((_, i) => (
        <div
          key={i}
          className="absolute rounded-full"
          style={{
            width: size * 0.28,
            height: size * 0.1,
            backgroundColor: color,
            // Trailing fade: the bar in front is solid, the one behind it dim.
            opacity: 0.15 + (i / bars) * 0.85,
            transform: `rotate(${i * (360 / bars)}deg) translate(${size * 0.34}px)`,
          }}
        />
      ))}
    </div>
  );
}
